<template>
  <div
    class="bg-white dark:bg-slate-900 rounded-md min-w-0 flex flex-col min-h-0 contact-info-panel-stack"
    :class="[
      { 'border-left': showAvatar },
      dynamicClasses,
      { 'w-full sm:w-[50%] md:w-[40%] lg:w-[30%]': !isModalView },
      conversationSidebarRootClasses,
    ]"
  >
    <div
      v-if="isModalView && contactModalContentLoading"
      class="contact-info-panel__loading-overlay"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div class="contact-info-panel__loading-spinner" aria-hidden="true" />
      <p class="contact-info-panel__loading-text">
        {{ $t('CONTACT_PROFILE.LOADING') }}
      </p>
    </div>
    <div
      class="w-full min-h-0 flex-1 overflow-y-auto hide-scrollbar"
      :class="{
        'flex flex-col': isMacrosOnly,
        'contact-info-panel-scroll--modal': isModalView,
        'contact-info-panel-scroll--conversation-overlay':
          isConversationSidebar && !isModalView,
      }"
    >
      <contact-info
        v-if="!isMacrosOnly"
        :key="resolvedContactId"
        :show-close-button="showCloseButton"
        :show-avatar="showAvatar"
        :contact="displayContact"
        :pipeline-contact="pipelineContact"
        :is-modal-view="isModalView"
        :is-conversation-sidebar="isConversationSidebar"
        :channel-type="effectiveChannelType"
        close-icon-name="dismiss"
        :reminder-flag="isAgentBotsFeatureEnabledReminder"
        @panel-close="onClose"
        @toggle-panel="onClose"
        @contact-deleted="onContactDeleted"
        @modal-content-loading="contactModalContentLoading = $event"
      />

      <!-- Conversation sidebar: payments follow contact header (unchanged). -->
      <div
        v-if="
          !isMacrosOnly && isAgentBotsFeatureEnabled && isConversationSidebar
        "
        class="contact-sidebar-section dark:border dark:border-slate-200"
      >
        <div>
          <div class="flex justify-between sidebar-section-title">
            <div>
              <h3 class="payment-heading">
                {{ $t('CONTACT_PANEL.SIDEBAR_SECTIONS.PREVIOUS_PAYMENTS') }}
              </h3>
            </div>
            <div>
              <woot-button class="import-clear" @click="PaymentModal">
                {{ $t('EXPORT_CONTACTS.NEW_PAYMENT') }}
              </woot-button>
            </div>
          </div>
          <div
            class="bg-gray-100 flex justify-between p-2 rounded-md table-header mt-1"
          >
            <div class="black-color w-1/4 flex justify-center items-center">
              Status
            </div>
            <div class="black-color w-1/4 flex justify-center items-center">
              Agent
            </div>
            <div class="black-color w-1/4 flex justify-center items-center">
              GST No.
            </div>
            <div class="black-color w-1/4 flex justify-center items-center">
              Amount
            </div>
          </div>
          <div class="max-h-[160px] payment-lists space-y-2 mt-2">
            <div
              v-if="
                ListAllPayment.length > 0 && !uiFlags.isFetchingPaymentContact
              "
            >
              <div
                v-for="(paymentRow, index) in ListAllPayment"
                :key="paymentRow.id"
                class="bg-gray-100 flex justify-between p-2 rounded-md"
                :class="{
                  'bg-slate-100 dark:bg-slate-700 flex p-2': index % 2 !== 0,
                }"
              >
                <div
                  class="font-semibold text-sm w-1/4 flex justify-center items-center"
                  :title="paymentRow.name"
                >
                  {{ truncateValue(paymentRow.name) }}
                </div>
                <div
                  class="text-sm w-1/4 text-gray-600 flex justify-center items-center"
                  :title="paymentRow?.agent?.name"
                >
                  {{ truncateValue(paymentRow?.agent?.name) || 'NA' }}
                </div>
                <div
                  class="flex w-1/4 justify-center text-sm items-center"
                  :title="paymentRow.gst_no"
                >
                  {{ truncateValue(paymentRow.gst_no) || 'NA' }}
                </div>
                <div
                  class="flex w-1/4 justify-center text-sm items-center text-green-500"
                  :title="paymentRow.amount"
                >
                  ₹{{ truncateValue(paymentRow.amount) }}
                </div>
              </div>
            </div>
            <div v-else-if="uiFlags.isFetchingPaymentContact">
              <loading-state
                v-if="uiFlags.isFetchingPaymentContact"
                :message="emptyStateMessage"
              />
            </div>
            <div
              v-else-if="!uiFlags.isFetchingPaymentContact"
              class="text-gray-500 text-center"
            >
              No previous payments found.
            </div>
          </div>
        </div>
      </div>

      <!-- Contacts / modal: previous conversations, then payments (not conversation sidebar). -->
      <template v-if="!isMacrosOnly && !isConversationSidebar">
        <div
          v-if="resolvedContactId && !hidePreviousConversationsSection"
          class="previous-conversation"
        >
          <div
            class="contact-sidebar-sections dark:border dark:border-slate-200"
          >
            <h3 class="sidebar-section-title dark:border dark:border-slate-200">
              {{ $t('CONTACT_PANEL.SIDEBAR_SECTIONS.PREVIOUS_CONVERSATIONS') }}
            </h3>
            <div class="max-h-[160px] previous-conversation-lists">
              <contact-conversations
                :contact-id="resolvedContactId"
                :conversation-id="conversationIdForContactConversationsList"
                :select-in-page="contactConversationsSelectInPage"
                :suppress-initial-loading="contactModalContentLoading"
                @conversation-selected="handleConversationSelected"
              />
            </div>
          </div>
        </div>
        <div
          v-if="isAgentBotsFeatureEnabled"
          class="contact-sidebar-section dark:border dark:border-slate-200"
        >
          <div>
            <div class="flex justify-between sidebar-section-title">
              <div>
                <h3 class="payment-heading">
                  {{ $t('CONTACT_PANEL.SIDEBAR_SECTIONS.PREVIOUS_PAYMENTS') }}
                </h3>
              </div>
              <div>
                <woot-button class="import-clear" @click="PaymentModal">
                  {{ $t('EXPORT_CONTACTS.NEW_PAYMENT') }}
                </woot-button>
              </div>
            </div>
            <div
              class="bg-gray-100 flex justify-between p-2 rounded-md table-header mt-1"
            >
              <div class="black-color w-1/4 flex justify-center items-center">
                Status
              </div>
              <div class="black-color w-1/4 flex justify-center items-center">
                Agent
              </div>
              <div class="black-color w-1/4 flex justify-center items-center">
                GST No.
              </div>
              <div class="black-color w-1/4 flex justify-center items-center">
                Amount
              </div>
            </div>
            <div class="max-h-[160px] payment-lists space-y-2 mt-2">
              <div
                v-if="
                  ListAllPayment.length > 0 && !uiFlags.isFetchingPaymentContact
                "
              >
                <div
                  v-for="(paymentRow, index) in ListAllPayment"
                  :key="paymentRow.id"
                  class="bg-gray-100 flex justify-between p-2 rounded-md"
                  :class="{
                    'bg-slate-100 dark:bg-slate-700 flex p-2': index % 2 !== 0,
                  }"
                >
                  <div
                    class="font-semibold text-sm w-1/4 flex justify-center items-center"
                    :title="paymentRow.name"
                  >
                    {{ truncateValue(paymentRow.name) }}
                  </div>
                  <div
                    class="text-sm w-1/4 text-gray-600 flex justify-center items-center"
                    :title="paymentRow?.agent?.name"
                  >
                    {{ truncateValue(paymentRow?.agent?.name) || 'NA' }}
                  </div>
                  <div
                    class="flex w-1/4 justify-center text-sm items-center"
                    :title="paymentRow.gst_no"
                  >
                    {{ truncateValue(paymentRow.gst_no) || 'NA' }}
                  </div>
                  <div
                    class="flex w-1/4 justify-center text-sm items-center text-green-500"
                    :title="paymentRow.amount"
                  >
                    ₹{{ truncateValue(paymentRow.amount) }}
                  </div>
                </div>
              </div>
              <div v-else-if="uiFlags.isFetchingPaymentContact">
                <loading-state
                  v-if="uiFlags.isFetchingPaymentContact"
                  :message="emptyStateMessage"
                />
              </div>
              <div
                v-else-if="!uiFlags.isFetchingPaymentContact"
                class="text-gray-500 text-center"
              >
                No previous payments found.
              </div>
            </div>
          </div>
        </div>
      </template>

      <div v-if="!isMacrosOnly">
        <new-payment
          :show="showPaymentModal"
          :contact="displayContact"
          @close="hidePaymentModal"
          @getNewPayment="getPaymentDetail"
        />
      </div>

      <!-- Macros-from-reply shortcut: dedicated panel mounted flush here. Keeping it inside
           vuedraggable left many empty `.list-group-item` rows above this block and pushed
           the header down inside the modal. -->
      <woot-feature-toggle
        v-if="isMacrosOnly"
        data-id="macros-section"
        feature-key="macros"
        class="macros-only-root min-h-0 flex-1 flex flex-col"
      >
        <macros-panel
          :conversation-id="effectiveConversationId"
          :show-close-button="true"
          class="min-h-0 flex-1"
          @close="onClose"
          @use-response="handleMacroResponse"
        />
      </woot-feature-toggle>

      <draggable
        v-else
        :list="sidebarItems"
        :disabled="!dragEnabled"
        class="list-group"
        ghost-class="ghost"
        handle=".drag-handle"
        @start="dragging = true"
        @end="onDragEnd"
      >
        <transition-group
          tag="div"
          class="contact-panel-accordion-list"
        >
          <div
            v-for="element in sidebarItems"
            :key="element.name"
            class="list-group-item"
          >
            <div
              v-if="
                isConversationSidebar &&
                  element.name === 'conversation_actions' &&
                  !isMacrosOnly
              "
              class="conversation--actions"
            >
              <accordion-item
                :title="
                  $t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_ACTIONS')
                "
                :is-open="isContactSidebarItemOpen('is_conv_actions_open')"
                :compact="!isModalView"
                @click="
                  value => toggleSidebarUIState('is_conv_actions_open', value)
                "
              >
                <conversation-action
                  :conversation-id="effectiveConversationId"
                  :inbox-id="effectiveInboxId"
                />
              </accordion-item>
            </div>

            <div
              v-else-if="
                isConversationSidebar &&
                  element.name === 'conversation_participants' &&
                  !isMacrosOnly
              "
              class="conversation--actions"
            >
              <accordion-item
                :title="$t('CONVERSATION_PARTICIPANTS.SIDEBAR_TITLE')"
                :is-open="isContactSidebarItemOpen('is_conv_participants_open')"
                :compact="!isModalView"
                @click="
                  value =>
                    toggleSidebarUIState('is_conv_participants_open', value)
                "
              >
                <conversation-participant
                  :conversation-id="effectiveConversationId"
                  :inbox-id="effectiveInboxId"
                />
              </accordion-item>
            </div>

            <div
              v-else-if="
                isConversationSidebar &&
                  element.name === 'conversation_info' &&
                  !isMacrosOnly
              "
            >
              <accordion-item
                :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_INFO')"
                :is-open="isContactSidebarItemOpen('is_conv_details_open')"
                :compact="!isModalView"
                @click="
                  value => toggleSidebarUIState('is_conv_details_open', value)
                "
              >
                <conversation-info
                  :conversation-attributes="conversationAdditionalAttributes"
                  :contact-attributes="contactAdditionalAttributes"
                />
              </accordion-item>
            </div>

            <div
              v-else-if="
                (isModalView || isConversationSidebar) &&
                  element.name === 'contact_notes' &&
                  !isMacrosOnly
              "
            >
              <accordion-item
                :title="$t('CONTACT_PANEL.SIDEBAR_SECTIONS.CONTACT_NOTES')"
                :is-open="
                  isContactSidebarItemOpen(
                    isConversationSidebar
                      ? 'is_contact_notes_open'
                      : 'is_ct_notes_open'
                  )
                "
                :compact="!isModalView"
                @click="
                  value =>
                    toggleSidebarUIState(
                      isConversationSidebar
                        ? 'is_contact_notes_open'
                        : 'is_ct_notes_open',
                      value
                    )
                "
              >
                <contact-notes
                  v-if="resolvedContactId"
                  :contact-id="resolvedContactId"
                  @note-content-changed="$emit('note-content-changed', $event)"
                />
              </accordion-item>
            </div>

            <div
              v-else-if="element.name === 'contact_attributes' && !isMacrosOnly"
            >
              <accordion-item
                :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_ATTRIBUTES')"
                :is-open="
                  isContactSidebarItemOpen(
                    isConversationSidebar
                      ? 'is_contact_attributes_open'
                      : 'is_ct_custom_attr_open'
                  )
                "
                :compact="!isModalView"
                @click="
                  value =>
                    toggleSidebarUIState(
                      isConversationSidebar
                        ? 'is_contact_attributes_open'
                        : 'is_ct_custom_attr_open',
                      value
                    )
                "
              >
                <custom-attributes
                  :contact-id="resolvedContactId"
                  :mycontact="displayContact"
                  attribute-type="contact_attribute"
                  attribute-class="conversation--attribute"
                  class="even"
                />
                <custom-attribute-selector
                  attribute-type="contact_attribute"
                  :contact-id="resolvedContactId"
                />
              </accordion-item>
            </div>

            <div
              v-else-if="
                isConversationSidebar &&
                  element.name === 'previous_conversation' &&
                  !isMacrosOnly
              "
            >
              <accordion-item
                v-if="resolvedContactId"
                :title="
                  $t('CONVERSATION_SIDEBAR.ACCORDION.PREVIOUS_CONVERSATION')
                "
                :is-open="isContactSidebarItemOpen('is_previous_conv_open')"
                :compact="!isModalView"
                @click="
                  value => toggleSidebarUIState('is_previous_conv_open', value)
                "
              >
                <contact-conversations
                  :contact-id="resolvedContactId"
                  :conversation-id="conversationIdForContactConversationsList"
                  :select-in-page="contactConversationsSelectInPage"
                  @conversation-selected="handleConversationSelected"
                />
              </accordion-item>
            </div>

            <div
              v-else-if="element.name === 'contact_attachment' && !isMacrosOnly"
            >
              <accordion-item
                :title="
                  $t('CONTACT_PANEL.SIDEBAR_SECTIONS.CONTACT_ATTACHMENTS')
                "
                :is-open="isContactSidebarItemOpen('is_ct_prev_attach_open')"
                :compact="!isModalView"
                @click="
                  value => toggleSidebarUIState('is_ct_prev_attach_open', value)
                "
              >
                <attachment-view
                  v-if="resolvedContactId"
                  :contact-id="resolvedContactId"
                />
              </accordion-item>
            </div>
          </div>
        </transition-group>
      </draggable>
    </div>
  </div>
</template>

<script>
import AccordionItem from 'dashboard/components/Accordion/AccordionItem';
import ContactConversations from 'dashboard/routes/dashboard/conversation/ContactConversations';
import ContactInfo from 'dashboard/routes/dashboard/conversation/contact/ContactInfo';
import ConversationAction from 'dashboard/routes/dashboard/conversation/ConversationAction.vue';
import ConversationParticipant from 'dashboard/routes/dashboard/conversation/ConversationParticipant.vue';
import ConversationInfo from 'dashboard/routes/dashboard/conversation/ConversationInfo';
import CustomAttributes from 'dashboard/routes/dashboard/conversation/customAttributes/CustomAttributes.vue';
import CustomAttributeSelector from 'dashboard/routes/dashboard/conversation/customAttributes/CustomAttributeSelector.vue';
import draggable from 'vuedraggable';
import uiSettingsMixin from 'dashboard/mixins/uiSettings';
import truncateMixin from 'dashboard/mixins/truncateMixin';
import AttachmentView from 'dashboard/routes/dashboard/conversation/contact/AttachmentView';
import NewPayment from '../../../../components/widgets/NewPayment.vue';
import { mixin as clickaway } from 'vue-clickaway';
import { mapGetters } from 'vuex';
import LoadingState from 'dashboard/components/widgets/LoadingState';
import ContactNotes from 'dashboard/modules/notes/NotesOnContactPage';
import MacrosPanel from 'dashboard/routes/dashboard/conversation/Macros/MacrosPanel';
import { FEATURE_FLAGS } from '../../../../featureFlags';
import { mergeContactRecord } from 'dashboard/helper/contactMeetingUiSync';

export default {
  components: {
    AccordionItem,
    ContactConversations,
    ContactInfo,
    ConversationAction,
    ConversationParticipant,
    ConversationInfo,
    CustomAttributes,
    CustomAttributeSelector,
    draggable,
    AttachmentView,
    NewPayment,
    LoadingState,
    ContactNotes,
    MacrosPanel,
  },
  mixins: [uiSettingsMixin, clickaway, truncateMixin],
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    onClose: {
      type: Function,
      default: () => {},
    },
    showAvatar: {
      type: Boolean,
      default: true,
    },
    showCloseButton: {
      type: Boolean,
      default: true,
    },
    pipelineContact: {
      type: Boolean,
      default: false,
    },
    isModalView: {
      type: Boolean,
      default: false,
    },
    /** When true, show conversation-only sections and use conversation sidebar order / persistence. */
    isConversationSidebar: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: [Number, String],
      default: undefined,
    },
    inboxId: {
      type: [Number, String],
      default: undefined,
    },
    openSource: {
      type: String,
      default: 'contact_header',
      validator: value => ['contact_header', 'macros_button'].includes(value),
    },
    channelType: {
      type: String,
      default: '',
    },
    /** When true, clicking a row in ContactConversations emits conversation-selected instead of a new tab. */
    contactConversationsSelectInPage: {
      type: Boolean,
      default: false,
    },
    /** Active conversation id for filtering/highlighting in the contact conversation list (e.g. contact profile page). */
    contactConversationsActiveId: {
      type: [String, Number],
      default: undefined,
    },
    /** When true, hide the compact "Previous conversations" block (e.g. contact profile uses tabs instead). */
    hidePreviousConversationsSection: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dragEnabled: true,
      sidebarItems: [],
      dragging: false,
      showPaymentModal: false,
      emptyStateMessage: 'Loading Payment History',
      contactModalContentLoading: false,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'payments/getUIFlags',
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      getContact: 'contacts/getContact',
      currentChat: 'getSelectedChat',
    }),
    isMacrosOnly() {
      return this.isConversationSidebar && this.openSource === 'macros_button';
    },
    effectiveConversationId() {
      if (this.conversationId != null && this.conversationId !== '') {
        return this.conversationId;
      }
      if (this.isConversationSidebar && this.currentChat?.id) {
        return this.currentChat.id;
      }
      return '';
    },
    effectiveInboxId() {
      if (this.inboxId != null && this.inboxId !== '') {
        return this.inboxId;
      }
      if (this.isConversationSidebar && this.currentChat?.inbox_id != null) {
        return this.currentChat.inbox_id;
      }
      return undefined;
    },
    effectiveChannelType() {
      if (this.channelType) {
        return this.channelType;
      }
      if (this.isConversationSidebar) {
        return this.currentChat?.meta?.channel || '';
      }
      return '';
    },
    resolvedContactId() {
      if (this.contact?.id) {
        return this.contact.id;
      }
      if (this.isConversationSidebar && this.currentChat?.meta?.sender?.id) {
        return this.currentChat.meta.sender.id;
      }
      return null;
    },
    displayContact() {
      const id = this.resolvedContactId;
      if (!id) {
        return this.contact || {};
      }
      const vuexContact = this.getContact(id);
      const base =
        this.contact?.id === id
          ? this.contact
          : this.currentChat?.meta?.sender || {};
      let merged = base;
      if (vuexContact?.id) {
        merged = mergeContactRecord(base, vuexContact);
      } else if (this.contact?.id) {
        merged = this.contact;
      } else {
        merged = { ...base, id };
      }
      return this.normalizeContactAssignee(merged);
    },
    conversationAdditionalAttributes() {
      if (!this.isConversationSidebar || !this.effectiveConversationId) {
        return {};
      }
      const meta = this.$store.getters[
        'conversationMetadata/getConversationMetadata'
      ](this.effectiveConversationId);
      return meta?.additional_attributes || {};
    },
    contactAdditionalAttributes() {
      return this.displayContact.additional_attributes || {};
    },
    conversationIdForPreviousList() {
      if (this.isConversationSidebar && this.effectiveConversationId) {
        return this.effectiveConversationId;
      }
      return '';
    },
    conversationIdForContactConversationsList() {
      const active = this.contactConversationsActiveId;
      if (active !== undefined && active !== null && active !== '') {
        return active;
      }
      return this.conversationIdForPreviousList;
    },
    conversationSidebarRootClasses() {
      if (!this.isConversationSidebar || this.isModalView) {
        return '';
      }
      return 'text-slate-900 dark:text-slate-300 border-slate-50 dark:border-slate-800/50 border-l rtl:border-l-0 rtl:border-r';
    },
    dynamicClasses() {
      return this.isModalView ? 'special-panel-styles' : 'contact--panel';
    },
    ListAllPayment() {
      return this.$store.getters['payments/ListAllPayment'] || [];
    },
    isAgentBotsFeatureEnabled() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        FEATURE_FLAGS.PAYMENT_DASHBOARD
      );
    },
    isAgentBotsFeatureEnabledReminder() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        FEATURE_FLAGS.SCHEDULE_DEMO
      );
    },
  },
  watch: {
    conversationId(newVal, prevVal) {
      if (
        !this.isConversationSidebar ||
        newVal === undefined ||
        newVal === null ||
        newVal === ''
      ) {
        return;
      }
      const next = String(newVal);
      const prev =
        prevVal === undefined || prevVal === null || prevVal === ''
          ? null
          : String(prevVal);
      if (next !== prev) {
        this.getContactDetails();
      }
    },
    resolvedContactId(newId, prevId) {
      const next =
        newId === undefined || newId === null || newId === ''
          ? null
          : String(newId);
      const prev =
        prevId === undefined || prevId === null || prevId === ''
          ? null
          : String(prevId);
      if (next && next !== prev) {
        this.refreshPayments();
        if (this.isConversationSidebar || this.isModalView) {
          this.getContactDetails();
        }
      }
    },
    openSource() {
      if (this.isConversationSidebar) {
        this.initializeSidebarItems();
      }
    },
  },
  mounted() {
    this.initializeSidebarItems();
    this.refreshPayments();
    if (this.isConversationSidebar) {
      this.getContactDetails();
      this.$store.dispatch('attributes/get', 0);
    } else if (this.isModalView && this.resolvedContactId) {
      this.getContactDetails();
    }
  },
  methods: {
    initializeSidebarItems() {
      if (this.isConversationSidebar) {
        let items = JSON.parse(
          JSON.stringify(this.conversationSidebarItemsOrder || [])
        );
        // Macros list/accordion is only for dedicated macros mode from the
        // conversation UI, not for the normal contact side panel (modal-style overlay).
        if (this.openSource !== 'macros_button') {
          items = items.filter(i => i.name !== 'macros');
        }
        this.sidebarItems = items;
      } else {
        const defaultItems = this.contactSidebarItemsOrder || [];
        this.sidebarItems = defaultItems.filter(
          item =>
            item.name !== 'contact_labels' &&
            item.name !== 'previous_conversation'
        );
        if (this.isModalView) {
          const hasContactNotes = this.sidebarItems.some(
            item => item.name === 'contact_notes'
          );
          if (!hasContactNotes) {
            this.sidebarItems.push({ name: 'contact_notes' });
          }
        }
      }
    },
    contactIdsMatch(firstId, secondId) {
      if (
        firstId === undefined ||
        firstId === null ||
        secondId === undefined ||
        secondId === null
      ) {
        return false;
      }
      return String(firstId) === String(secondId);
    },
    normalizeContactAssignee(contact) {
      if (!contact?.id) {
        return contact || {};
      }
      const assigneeId = contact.assignee_id;
      if (assigneeId == null || assigneeId === '') {
        return contact;
      }
      if (
        contact.assignee?.id &&
        !this.contactIdsMatch(contact.assignee.id, assigneeId)
      ) {
        const { assignee, ...rest } = contact;
        return rest;
      }
      return contact;
    },
    getContactDetails() {
      if (this.resolvedContactId) {
        this.$store.dispatch('contacts/show', { id: this.resolvedContactId });
      }
    },
    refreshPayments() {
      if (!this.resolvedContactId) {
        return;
      }
      this.$store.dispatch('payments/AllPaymentContactList', {
        key: 'contact_id',
        value: this.resolvedContactId,
      });
    },
    onDragEnd() {
      this.dragging = false;
      if (this.isConversationSidebar) {
        this.updateUISettings({
          conversation_sidebar_items_order: this.sidebarItems,
        });
      } else {
        const contactOrder = [
          { name: 'previous_conversation' },
          ...this.sidebarItems,
        ];
        this.updateUISettings({
          contact_sidebar_items_order: contactOrder,
        });
      }
    },
    handleConversationSelected(conversation) {
      this.$emit('conversation-selected', conversation);
    },
    hidePaymentModal() {
      this.showPaymentModal = false;
    },
    PaymentModal() {
      this.showPaymentModal = true;
    },
    getPaymentDetail() {
      this.refreshPayments();
    },
    onContactDeleted() {
      this.$emit('contact-deleted');
    },
    handleMacroResponse(messageBody) {
      if (typeof window !== 'undefined' && window.bus) {
        window.bus.$emit('insert-message-to-reply-box', messageBody);
      }
      this.onClose();
    },
  },
};
</script>

<style lang="scss" scoped>
:deep {
  .contact--profile {
    @apply pb-3 mb-4;
  }
  .conversation--actions .multiselect-wrap--small {
    .multiselect {
      @apply box-border pl-6;
    }
    .multiselect__element {
      span {
        @apply w-full;
      }
    }
  }
}
.contact--panel {
  @apply border-r border-slate-50 dark:border-slate-800/50 h-full text-sm overflow-y-auto relative;
}

.list-group {
  .list-group-item {
    @apply bg-white dark:bg-slate-900;
    padding: 0 !important;
  }
}

.contact-info-panel-stack .contact-panel-accordion-list {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  width: 100%;
}

/* AccordionItem root uses Tailwind -mt-px to stack flush in the conversation sidebar */
.contact-info-panel-stack .list-group :deep([class*='-mt-px']) {
  margin-top: 0 !important;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  background-color: #fff;
}

body.dark .contact-info-panel-stack .list-group :deep([class*='-mt-px']),
[data-theme='dark'] .contact-info-panel-stack .list-group :deep([class*='-mt-px']) {
  border-color: #475569;
  background-color: transparent;
}

.contact-info-panel-stack .list-group {
  margin-left: 0.75rem;
  margin-right: 0.75rem;
}

.contact-info-panel-stack .list-group :deep(button.drag-handle) {
  padding: 0.625rem 0.75rem !important;
  width: 100%;
  box-sizing: border-box;
}

.conversation--details {
  @apply py-0 px-4;
}
/*
 * Dropdowns only (not the contact panel — see .special-panel-styles z-index: 40):
 * - Primary sidebar: z-index 33 (Primary.vue)
 * - Secondary flyout dim layer: z-index 25 (Secondary.vue .overlay)
 * Cap popovers below that overlay while staying above in-panel chrome like z-[10].
 */
.contact-info-panel-stack {
  --z-index-very-high: 22;
  --z-index-highest: 23;
}

.contact-info-panel-stack :deep(.z-50) {
  z-index: 22 !important;
}

.contact-info-panel-stack :deep(.multiselect__content-wrapper) {
  z-index: 22 !important;
}

/* .dropdown-pane uses var(--z-index-very-high); explicit rule covers edge resets */
.contact-info-panel-stack :deep(.dropdown-pane) {
  z-index: 22 !important;
}

.special-panel-styles {
  position: fixed;
  box-shadow: 4px 4px 10px rgba(0, 0, 0, 0.5);
  top: 10vh;
  bottom: 14vh;
  left: 50%;
  z-index: 40;
  width: 35%;
  height: auto;
  max-height: none;
  transform: translateX(-50%);
  overflow: hidden;
  scrollbar-width: none;
}

.special-panel-styles .contact-info-panel-scroll--modal,
.contact-info-panel-scroll--conversation-overlay {
  padding-bottom: 1.25rem;
}

.contact-info-panel__loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 45;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 1.5rem;
  background: rgb(255 255 255 / 0.45);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  cursor: wait;
  pointer-events: all;
}

.contact-info-panel__loading-spinner {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 3px solid #d6e6f8;
  border-top-color: #1b7edb;
  animation: contact-info-panel-loading-spin 0.7s linear infinite;
}

.contact-info-panel__loading-text {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: #334155;
  text-align: center;
  letter-spacing: -0.01em;
  line-height: 1.35;
}

@keyframes contact-info-panel-loading-spin {
  to {
    transform: rotate(360deg);
  }
}

body.dark .contact-info-panel__loading-overlay {
  background: rgb(15 23 42 / 0.55);
}

body.dark .contact-info-panel__loading-spinner {
  border-color: rgb(51 65 85 / 0.9);
  border-top-color: #60a5fa;
}

body.dark .contact-info-panel__loading-text {
  color: #e2e8f0;
}

.sidebar-section-title {
  font-size: 12px;
  font-weight: 700;
  color: #8e9499;
  padding: 0px 0px 9px;
  border-bottom: 1px solid #e5e7eb;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.contact-sidebar-section {
  border-radius: 8px;
  margin: 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  padding: 10px;
  transition: box-shadow 0.3s ease;
}
.contact-sidebar-sections {
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  padding: 10px;
  margin: 12px;
  transition: box-shadow 0.3s ease;
}
.payment-heading {
  font-size: 12px;
  font-weight: 600;
  padding-top: 12px;
  color: #8e9499;
}
.import-clear {
  font-size: 12px;
}
.w-20 {
  width: 20%;
}
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
.previous-conversation {
  border-radius: 10px;
}
.payment-lists {
  @apply overflow-hidden hover:overflow-y-auto;
}
.previous-conversation-lists {
  @apply overflow-hidden hover:overflow-y-auto;
}
</style>
